import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';

/** The signal that surfaced a mined draft. */
export type KnowledgeDraftSignal = 'bad_feedback' | 'failed_sql';

/** Artifact a draft would become once approved. */
export type KnowledgeArtifactType = 'lesson' | 'verified_query' | 'catalog_entry' | 'skill';

/** Review state on a draft. Mirrors the backend `status` column. */
export type KnowledgeDraftStatus = 'pending' | 'approved' | 'rejected';

/** How the draft entered the queue. */
export type KnowledgeDraftSource = 'mining' | 'capture';

/** One recorded occurrence backing a mined skill draft. */
export interface KnowledgeDraftSighting {
    conversationId?: number;
    occurredAt?: string;
}

/**
 * Evidence the mining job attached to a draft — the cluster of bad feedback
 * or failed SQL that justified surfacing it. All but `signal` are optional
 * because the shape varies by signal type.
 */
export interface KnowledgeDraftEvidence {
    signal: KnowledgeDraftSignal;
    clusterSize?: number;
    weightedScore?: number;
    view?: string;
    sourceEventIds?: number[];
    sampleQuestions?: string[];
    sampleSql?: string[];
    chips?: string[];
    sightings?: KnowledgeDraftSighting[];
}

/** Editable payload of a draft lesson — what the human refines before approving. */
export interface KnowledgeDraftPayload {
    title?: string;
    triggerContext?: string;
    guidance?: string;
    /** Skill drafts (artifactType 'skill') use name/description/body/fingerprint instead. */
    name?: string;
    description?: string;
    body?: string;
    fingerprint?: string;
}

/** One mined draft awaiting review. */
export interface KnowledgeDraft {
    id: number;
    artifactType: KnowledgeArtifactType;
    status: KnowledgeDraftStatus;
    proposedPayload: KnowledgeDraftPayload;
    evidence: KnowledgeDraftEvidence | null;
    priority: number;
    source: KnowledgeDraftSource;
    createdAt: string | null;
}

/** A candidate verified query awaiting promotion. */
export interface CandidateQuery {
    id: number;
    title: string;
    questionPattern: string;
    verifiedSql: string;
    resultShape: string | null;
    notes: string | null;
    status: string;
    sourceConversationId: number | null;
    createdAt: string | null;
    embedStatus?: 'ok' | 'pending';
    fireCount: number;
    lastFiredAt: string | null;
    approvedAt: string | null;
    autoPromoted: boolean;
    retireReason?: string | null;
}

/** Body for PATCH on a draft. All fields optional — send only what changed. */
export interface KnowledgeDraftInput {
    title?: string;
    triggerContext?: string;
    guidance?: string;
}

/**
 * Thin client for the Gable review-queue API — the approval surface for the
 * self-improving loop. The mining job surfaces draft lessons and candidate
 * verified queries; reviewers refine and approve (promote) or reject (retire)
 * them here. Super-only routes — the backend gate refuses non-super even if
 * these helpers got called.
 */
@Injectable({ providedIn: 'root' })
export class GableKnowledgeService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    /** Mined drafts, candidate verified queries, and the verified query ledger (live + retired) in one shot. */
    listDrafts(): Observable<{
        drafts: KnowledgeDraft[];
        candidateQueries: CandidateQuery[];
        verifiedQueries: CandidateQuery[];
        retiredQueries: CandidateQuery[];
    }> {
        const jwt = this.requireJwt();
        return this._http.get<{
            drafts: KnowledgeDraft[];
            candidateQueries: CandidateQuery[];
            verifiedQueries: CandidateQuery[];
            retiredQueries: CandidateQuery[];
        }>(Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeDrafts(jwt.orgId)));
    }

    /** Inline-edit a draft's title / trigger / guidance. */
    updateDraft(id: number, body: KnowledgeDraftInput): Observable<{ draft: KnowledgeDraft }> {
        const jwt = this.requireJwt();
        return this._http.patch<{ draft: KnowledgeDraft }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeDraftItem(jwt.orgId, id)),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    /**
     * Promote a draft into a real lesson. Returns the new lesson id. The
     * backend returns HTTP 422 if title / triggerContext / guidance are empty
     * — callers should surface that message via the growler.
     */
    approveDraft(id: number): Observable<{ draft: KnowledgeDraft; lessonId: number }> {
        const jwt = this.requireJwt();
        return this._http.post<{ draft: KnowledgeDraft; lessonId: number }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeDraftApprove(jwt.orgId, id)),
            this._http.serializeObject({} as { [k: string]: any }),
        );
    }

    /** Reject (retire) a draft so it stops showing in the queue. */
    rejectDraft(id: number): Observable<{ draft: KnowledgeDraft }> {
        const jwt = this.requireJwt();
        return this._http.post<{ draft: KnowledgeDraft }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeDraftReject(jwt.orgId, id)),
            this._http.serializeObject({} as { [k: string]: any }),
        );
    }

    /** Promote a candidate query to a verified query. */
    approveVerifiedQuery(id: number): Observable<{ query: CandidateQuery }> {
        const jwt = this.requireJwt();
        return this._http.post<{ query: CandidateQuery }>(
            Routes.MakeLegFiCoreUrl(
                Routes.GableModule.KnowledgeVerifiedQueryApprove(jwt.orgId, id),
            ),
            this._http.serializeObject({} as { [k: string]: any }),
        );
    }

    /** Retire a candidate query. */
    rejectVerifiedQuery(id: number): Observable<{ query: CandidateQuery }> {
        const jwt = this.requireJwt();
        return this._http.post<{ query: CandidateQuery }>(
            Routes.MakeLegFiCoreUrl(
                Routes.GableModule.KnowledgeVerifiedQueryReject(jwt.orgId, id),
            ),
            this._http.serializeObject({} as { [k: string]: any }),
        );
    }

    /** Retire a live verified query — instant kill switch, kept for audit. */
    retireVerifiedQuery(id: number, reason?: string): Observable<{ query: CandidateQuery }> {
        const jwt = this.requireJwt();
        const body: { reason?: string } = {};
        if (reason && reason.trim().length > 0) {
            body.reason = reason.trim();
        }
        return this._http.post<{ query: CandidateQuery }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeVerifiedQueryRetire(jwt.orgId, id)),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    /** Restore a retired verified query back to live. */
    restoreVerifiedQuery(id: number): Observable<{ query: CandidateQuery }> {
        const jwt = this.requireJwt();
        return this._http.post<{ query: CandidateQuery }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.KnowledgeVerifiedQueryRestore(jwt.orgId, id)),
            this._http.serializeObject({} as { [k: string]: any }),
        );
    }

    private requireJwt(): JwtLegFiClaims {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT — cannot call knowledge endpoint');
        }
        return jwt;
    }
}
